import { supabase } from '../supabaseClient';
import { buildListHeadVersion } from './platformGlobalAdminCache';
import { canWriteSchoolScopedDataCache, invalidateSchoolScopedDataCaches } from './schoolDataCacheVersion';
import { recordDevLocalStorageProgramCacheUse } from './devNetworkTransferMonitor';

export type AnnualProjectProgram = {
  id: string;
  name: string;
  colorHex: string;
  termNumber: 1 | 2;
};

const pendingLoads = new Set<string>();

/** 서버 fetch 도중 로컬 저장이 있었는지 판별(늦게 도착한 스냅샷으로 덮어쓰기 방지) */
let annualProjectProgramsCacheWriteGeneration = 0;

export function getAnnualProjectProgramsCacheWriteGeneration(): number {
  return annualProjectProgramsCacheWriteGeneration;
}

/** v2: `{ programs, version }` — version은 id:updated_at head 시그니처 */
function keyV2(workspaceId: string, gradeId: string, academicYear: number): string {
  return `cp.annualProjectPrograms.v2.${workspaceId}.${gradeId}.${academicYear}`;
}

function keyV1(workspaceId: string, gradeId: string, academicYear: number): string {
  return `cp.annualProjectPrograms.v1.${workspaceId}.${gradeId}.${academicYear}`;
}

function isHex6(v: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(v);
}

function normalize(raw: unknown): AnnualProjectProgram[] {
  if (!Array.isArray(raw)) return [];
  const out: AnnualProjectProgram[] = [];
  raw.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const o = row as Record<string, unknown>;
    const id = typeof o.id === 'string' && o.id.trim() ? o.id.trim() : '';
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    const colorHex = typeof o.colorHex === 'string' ? o.colorHex.trim() : '';
    const tn = o.termNumber === 1 || o.termNumber === 2 ? o.termNumber : null;
    if (!id || !name || !isHex6(colorHex) || tn == null) return;
    out.push({ id, name, colorHex, termNumber: tn });
  });
  return out;
}

export type AnnualProjectProgramsCacheSnapshot = {
  found: boolean;
  programs: AnnualProjectProgram[];
  /** head 시그니처. 없으면(레거시 배열 캐시) 항상 원격과 재비교 */
  version: string | null;
  /** 로그인·새로고침 동기화에서 annualProjects 파트가 바뀌어 DB로 교체해야 함 */
  stale: boolean;
};

export function readAnnualProjectProgramsCacheSnapshot(
  workspaceId: string,
  gradeId: string,
  academicYear: number
): AnnualProjectProgramsCacheSnapshot {
  const missing: AnnualProjectProgramsCacheSnapshot = { found: false, programs: [], version: null, stale: false };
  if (!workspaceId || !gradeId || typeof window === 'undefined') return missing;
  try {
    const rawV2 = window.localStorage.getItem(keyV2(workspaceId, gradeId, academicYear));
    if (rawV2) {
      const parsed = JSON.parse(rawV2) as { programs?: unknown; version?: unknown; stale?: unknown } | unknown[];
      if (Array.isArray(parsed)) {
        return { found: true, programs: normalize(parsed), version: null, stale: false };
      }
      return {
        found: true,
        programs: normalize(parsed?.programs),
        version: typeof parsed?.version === 'string' && parsed.version ? parsed.version : null,
        stale: parsed?.stale === true,
      };
    }
    const rawV1 = window.localStorage.getItem(keyV1(workspaceId, gradeId, academicYear));
    if (rawV1) {
      return { found: true, programs: normalize(JSON.parse(rawV1)), version: null, stale: false };
    }
    return missing;
  } catch {
    return missing;
  }
}

export function hasAnnualProjectProgramsCacheEntry(
  workspaceId: string,
  gradeId: string,
  academicYear: number
): boolean {
  return readAnnualProjectProgramsCacheSnapshot(workspaceId, gradeId, academicYear).found;
}

/** localStorage만 읽고, 백그라운드 fetch는 건드리지 않음(네트워크 실패 폴백용) */
export function readAnnualProjectProgramsCache(
  workspaceId: string,
  gradeId: string,
  academicYear: number
): AnnualProjectProgram[] {
  return readAnnualProjectProgramsCacheSnapshot(workspaceId, gradeId, academicYear).programs;
}

/**
 * 서버와 맞춘 적 있고(version) 교체 표시가 없는 캐시는 그대로 쓴다.
 * 원격 변경은 로그인·새로고침의 annualProjects 파트 revision 비교가 stale로 표시한다.
 */
export function isAnnualProjectProgramsSnapshotUsable(snapshot: AnnualProjectProgramsCacheSnapshot): boolean {
  return snapshot.found && !!snapshot.version && !snapshot.stale;
}

export function loadAnnualProjectPrograms(
  workspaceId: string,
  gradeId: string,
  academicYear: number
): AnnualProjectProgram[] {
  if (!workspaceId || !gradeId) return [];
  const loadKey = `${workspaceId}|${gradeId}|${academicYear}`;
  const snapshot = readAnnualProjectProgramsCacheSnapshot(workspaceId, gradeId, academicYear);
  const cached = snapshot.programs;
  /** LS를 즉시 쓰고, 캐시가 없거나 교체 표시(stale)·레거시일 때만 백그라운드로 DB와 맞춘다 */
  if (isAnnualProjectProgramsSnapshotUsable(snapshot)) return cached;
  if (!pendingLoads.has(loadKey)) {
    pendingLoads.add(loadKey);
    void getAnnualProjectPrograms(workspaceId, gradeId, academicYear).finally(() => {
      pendingLoads.delete(loadKey);
    });
  }
  return cached;
}

export const ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT = 'cp-annual-project-programs-changed';

function emitAnnualProjectProgramsChanged(workspaceId: string, gradeId: string, academicYear: number): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT, {
      detail: { workspaceId, gradeId, academicYear },
    })
  );
}

export type CacheAnnualProjectProgramsOptions = { force?: boolean; version?: string | null };

export function cacheAnnualProjectPrograms(
  workspaceId: string,
  gradeId: string,
  academicYear: number,
  list: AnnualProjectProgram[],
  options?: CacheAnnualProjectProgramsOptions
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const payload = {
      programs: list,
      version: typeof options?.version === 'string' ? options.version : null,
      savedAt: Date.now(),
    };
    window.localStorage.setItem(keyV2(workspaceId, gradeId, academicYear), JSON.stringify(payload));
    try {
      window.localStorage.removeItem(keyV1(workspaceId, gradeId, academicYear));
    } catch {
      /* ignore */
    }
    annualProjectProgramsCacheWriteGeneration += 1;
    emitAnnualProjectProgramsChanged(workspaceId, gradeId, academicYear);
  } catch {
    /* ignore quota */
  }
}

function buildProgramsHeadVersion(
  rows: Array<{ id: string; updated_at?: string | null }>
): string {
  return buildListHeadVersion(
    rows.map((r) => ({
      id: String(r.id),
      updated_at: typeof r.updated_at === 'string' ? r.updated_at : '',
    }))
  );
}

async function fetchAnnualProjectProgramsFull(
  workspaceId: string,
  gradeId: string,
  academicYear: number
): Promise<{ programs: AnnualProjectProgram[]; version: string } | null> {
  const { data, error } = await supabase
    .from('annual_project_programs')
    .select('id, name, color_hex, term_number, updated_at')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('academic_year', academicYear)
    .order('term_number', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) return null;
  const records = (data ?? []) as Array<{
    id: string;
    name: string;
    color_hex: string;
    term_number: 1 | 2;
    updated_at?: string | null;
  }>;
  const programs = records.map((row) => ({
    id: row.id,
    name: row.name ?? '',
    colorHex: row.color_hex ?? '',
    termNumber: row.term_number,
  }));
  return { programs, version: buildProgramsHeadVersion(records) };
}

export type GetAnnualProjectProgramsOptions = { forceRefresh?: boolean };

/**
 * 서버와 맞춘 캐시(version 있음·stale 아님)는 네트워크 없이 재사용한다.
 * 캐시 없음·레거시·stale(다른 기기에서 annualProjects 변경)·강제 갱신이면 DB 풀 로드 후 캐시 교체.
 */
export async function getAnnualProjectPrograms(
  workspaceId: string,
  gradeId: string,
  academicYear: number,
  options?: GetAnnualProjectProgramsOptions
): Promise<AnnualProjectProgram[]> {
  if (!workspaceId || !gradeId) return [];
  const snapshot = readAnnualProjectProgramsCacheSnapshot(workspaceId, gradeId, academicYear);
  const fallback = (): AnnualProjectProgram[] => snapshot.programs;

  if (!options?.forceRefresh && isAnnualProjectProgramsSnapshotUsable(snapshot)) {
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('연간프로젝트·LS');
    }
    return snapshot.programs;
  }

  const writeGenAtFetchStart = getAnnualProjectProgramsCacheWriteGeneration();
  try {
    const fetched = await fetchAnnualProjectProgramsFull(workspaceId, gradeId, academicYear);
    if (!fetched) return fallback();

    /**
     * 레거시(버전 없는) 로컬만 있고 DB가 비어 있으면 1회 업로드(기기 이전 마이그레이션).
     * 이미 서버와 동기화된 캐시(version 있음)인데 DB가 비면 원격(빈 목록)을 신뢰한다.
     */
    if (fetched.programs.length === 0 && snapshot.programs.length > 0 && snapshot.version == null) {
      try {
        await saveAnnualProjectPrograms(workspaceId, gradeId, academicYear, snapshot.programs);
      } catch {
        /* 오프라인 등: 서버 동기화 생략, 로컬 캐시 유지 */
      }
      return snapshot.programs;
    }

    if (getAnnualProjectProgramsCacheWriteGeneration() !== writeGenAtFetchStart) {
      const cur = readAnnualProjectProgramsCache(workspaceId, gradeId, academicYear);
      return cur.length > 0 ? cur : fetched.programs;
    }
    cacheAnnualProjectPrograms(workspaceId, gradeId, academicYear, fetched.programs, {
      force: true,
      version: fetched.version,
    });
    return fetched.programs;
  } catch {
    return fallback();
  }
}

export async function saveAnnualProjectPrograms(
  workspaceId: string,
  gradeId: string,
  academicYear: number,
  list: AnnualProjectProgram[]
): Promise<void> {
  if (!workspaceId || !gradeId) return;
  const normalized = normalize(list);
  const savedAt = new Date().toISOString();
  const payload = normalized.map((row) => ({
    id: row.id,
    workspace_id: workspaceId,
    grade_id: gradeId,
    academic_year: academicYear,
    term_number: row.termNumber,
    name: row.name,
    color_hex: row.colorHex,
    updated_at: savedAt,
  }));
  /** 저장 중 UI 즉시 반영(버전은 DB 성공 후 확정) */
  cacheAnnualProjectPrograms(workspaceId, gradeId, academicYear, normalized, { force: true, version: null });

  const { data: existing, error: existingError } = await supabase
    .from('annual_project_programs')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('academic_year', academicYear);
  if (existingError) throw new Error(existingError.message);

  if (payload.length > 0) {
    const { error } = await supabase.from('annual_project_programs').upsert(payload, { onConflict: 'id' });
    if (error) throw new Error(error.message);
  }

  const keepIds = new Set(payload.map((row) => row.id));
  const removeIds = ((existing ?? []) as Array<{ id: string }>)
    .map((row) => row.id)
    .filter((id) => !keepIds.has(id));
  if (removeIds.length > 0) {
    const { error } = await supabase.from('annual_project_programs').delete().in('id', removeIds);
    if (error) throw new Error(error.message);
  }
  if (payload.length === 0) {
    const { error } = await supabase
      .from('annual_project_programs')
      .delete()
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .eq('academic_year', academicYear);
    if (error) throw new Error(error.message);
  }
  await invalidateSchoolScopedDataCaches(workspaceId, ['annualProjects']);
  const version = buildProgramsHeadVersion(payload);
  cacheAnnualProjectPrograms(workspaceId, gradeId, academicYear, normalized, { force: true, version });
}

/** #RRGGBB → 소문자 (유효하지 않으면 null) */
export function normalizeProjectColorHex6(colorHex: string): string | null {
  const t = colorHex.trim();
  if (!isHex6(t)) return null;
  return t.toLowerCase();
}

/**
 * 연간 셀·프로젝트 등록 색을 동일 규칙으로 맞춘 뒤 비교한다 (#RGB, # 없는 6자, 대소문자).
 */
export function normalizeHexColorForProjectCompare(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim();
  if (!t) return null;
  let s = t.toLowerCase();
  if (!s.startsWith('#')) {
    if (/^[0-9a-f]{6}$/.test(s)) s = `#${s}`;
    else return null;
  }
  if (/^#[0-9a-f]{6}$/.test(s)) return s;
  if (/^#[0-9a-f]{3}$/.test(s)) {
    const r = s[1] ?? '';
    const g = s[2] ?? '';
    const b = s[3] ?? '';
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return null;
}

/**
 * 같은 학기(term)에 이미 배정된 프로젝트 색(hex 소문자).
 * 수정 중이면 excludeProjectId에 해당하는 항목은 제외(현재 프로젝트 색은 허용).
 */
export function getUsedProjectColorHexSetForTerm(
  programs: AnnualProjectProgram[],
  termNumber: 1 | 2,
  excludeProjectId?: string | null
): Set<string> {
  const out = new Set<string>();
  programs.forEach((p) => {
    if (p.termNumber !== termNumber) return;
    if (excludeProjectId && p.id === excludeProjectId) return;
    const n = normalizeProjectColorHex6(p.colorHex);
    if (n) out.add(n);
  });
  return out;
}

/** 같은 학기에 다른 프로젝트가 이미 이 색을 쓰는지 (수정 시 본인 제외) */
export function isOtherProjectUsingColorForTerm(
  colorHex: string,
  programs: AnnualProjectProgram[],
  termNumber: 1 | 2,
  excludeProjectId?: string | null
): boolean {
  const n = normalizeProjectColorHex6(colorHex);
  if (!n) return false;
  return getUsedProjectColorHexSetForTerm(programs, termNumber, excludeProjectId).has(n);
}

/** 선택한 학기 중 하나라도 이미 이 색을 쓰는지 (1·2학기 동시 생성용) */
export function isOtherProjectUsingColorForAnyOfTerms(
  colorHex: string,
  programs: AnnualProjectProgram[],
  termNumbers: readonly (1 | 2)[],
  excludeProjectId?: string | null
): boolean {
  if (termNumbers.length === 0) return false;
  return termNumbers.some((term) =>
    isOtherProjectUsingColorForTerm(colorHex, programs, term, excludeProjectId)
  );
}

/**
 * 셀 배경색이 해당 학기에 등록된 프로젝트 색과 같으면 프로젝트 시간으로 간주한다.
 * (시간표명은 유지·프로젝트는 색만 칠하는 경우 포함. 연간시간표 격자 무늈·연간진도표 구분과 동일 규칙)
 *
 * 기초시간표 제외 색과 프로젝트가 동일 hex를 쓰는 경우, 제외는 단색·프로젝트는 무늈 배경으로 구분하므로
 * `isBaseExcluded === true`이면 프로젝트로 보지 않는다.
 */
export function matchesAnnualProjectProgramCell(
  colorHex: string | null | undefined,
  programs: AnnualProjectProgram[],
  termNumber: 1 | 2,
  isBaseExcluded?: boolean
): boolean {
  if (isBaseExcluded) return false;
  const nc = normalizeHexColorForProjectCompare(colorHex);
  if (!nc) return false;
  return programs.some((p) => {
    if (p.termNumber !== termNumber) return false;
    const np = normalizeHexColorForProjectCompare(p.colorHex);
    return np !== null && np === nc;
  });
}
